import React, { useEffect, useRef } from 'react';
import { Box, Container, Typography, Button, Stack, useTheme, useMediaQuery, Chip } from '@mui/material';
import { 
  AutoAwesome, 
  East,
  Security,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import { scroller } from 'react-scroll';
import HeroPipeline from './HeroPipeline';
import MagneticButton from './MagneticButton';

function Hero() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isLight, isHydrated } = useThemeMode();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const mousePositionRef = useRef({ x: 0.5, y: 0.5 });
  const isHoveredRef = useRef(false);
  const visibleRef = useRef(true);

  // ── Floating particles ──
  useEffect(() => {
    if (!canvasRef.current || isMobile) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const particles = [];

    const initializeParticles = () => {
      particles.length = 0;
      const count = Math.min(40, Math.floor(canvas.width * canvas.height / 18000));
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: 0, vy: 0,
          baseVx: (Math.random() - 0.5) * 0.25,
          baseVy: (Math.random() - 0.5) * 0.15 - 0.08,
          size: Math.random() * 2.2 + 0.8,
          opacity: Math.random() * 0.4 + 0.15,
          pulse: Math.random() * Math.PI * 2,
          pulseSpeed: Math.random() * 0.012 + 0.004,
          noiseOffsetX: Math.random() * 1000,
          noiseOffsetY: Math.random() * 1000,
          noiseSpeed: Math.random() * 0.0006 + 0.0003,
          noiseAmplitude: Math.random() * 0.35 + 0.15,
        });
      }
    };

    const setCanvasDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        canvas.width = width;
        canvas.height = height;
        initializeParticles();
      }
    };

    setCanvasDimensions();
    window.addEventListener('resize', setCanvasDimensions);
    let animationFrame;
    let time = 0;

    // Pre-compute color strings to avoid template literal allocations per frame
    const GLOW_OUTER_LIGHT = 'rgba(129,140,248,';
    const GLOW_OUTER_DARK  = 'rgba(56,189,248,';
    const GLOW_INNER_LIGHT = 'rgba(99,102,241,';
    const GLOW_INNER_DARK  = 'rgba(16,185,129,';
    const DOT_LIGHT        = 'rgba(99,102,241,';
    const DOT_DARK         = 'rgba(255,255,255,';
    const LINE_LIGHT       = 'rgba(99,102,241,';
    const LINE_DARK        = 'rgba(16,185,129,';

    const animate = () => {
      if (!visibleRef.current) { animationFrame = null; return; }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.016;

      const goBase = isLight ? GLOW_OUTER_LIGHT : GLOW_OUTER_DARK;
      const goMul  = isLight ? 0.06 : 0.10;
      const giBase = isLight ? GLOW_INNER_LIGHT : GLOW_INNER_DARK;
      const giMul  = isLight ? 0.18 : 0.35;
      const dotBase = isLight ? DOT_LIGHT : DOT_DARK;
      const dotMul  = isLight ? 0.6 : 1.15;
      const lineBase = isLight ? LINE_LIGHT : LINE_DARK;
      const lineMul  = isLight ? 0.06 : 0.1;

      for (let idx = 0; idx < particles.length; idx++) {
        const p = particles[idx];
        const nx = p.noiseOffsetX + time * p.noiseSpeed;
        const ny = p.noiseOffsetY + time * p.noiseSpeed;
        const noiseX = (Math.sin(nx * 6.28) + Math.sin(nx * 2.17 + 1.3) * 0.5) * p.noiseAmplitude;
        const noiseY = (Math.sin(ny * 6.28 + 0.7) + Math.sin(ny * 2.63 + 0.9) * 0.5) * p.noiseAmplitude;

        let tvx = p.baseVx + noiseX;
        let tvy = p.baseVy + noiseY;

        if (isHoveredRef.current) {
          const mx = mousePositionRef.current.x * canvas.width;
          const my = mousePositionRef.current.y * canvas.height;
          const dx = mx - p.x, dy = my - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 250 && dist > 1) {
            const influence = Math.pow(1 - dist / 250, 2) * 1.5;
            const repel = dist < 35 ? -0.5 : 1;
            tvx += (dx / dist) * influence * repel;
            tvy += (dy / dist) * influence * repel;
          }
        }

        p.vx += (tvx - p.vx) * 0.08;
        p.vy += (tvy - p.vy) * 0.08;
        const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (spd > 2) { p.vx = (p.vx / spd) * 2; p.vy = (p.vy / spd) * 2; }

        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        p.pulse += p.pulseSpeed;
        const op = p.opacity + Math.sin(p.pulse) * 0.15;
        const sz = p.size + Math.sin(p.pulse * 1.5) * 0.4;

        // Glow — layered circles, pre-computed color bases
        ctx.fillStyle = goBase + (op * goMul) + ')';
        ctx.beginPath(); ctx.arc(p.x, p.y, sz * 3.5, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = giBase + (op * giMul) + ')';
        ctx.beginPath(); ctx.arc(p.x, p.y, sz * 1.8, 0, Math.PI * 2); ctx.fill();

        // Core dot
        ctx.fillStyle = dotBase + (op * dotMul) + ')';
        ctx.beginPath(); ctx.arc(p.x, p.y, sz, 0, Math.PI * 2); ctx.fill();

        // Connections — index-based loop, no .slice()
        for (let j = idx + 1; j < particles.length; j++) {
          const q = particles[j];
          const cdx = p.x - q.x, cdy = p.y - q.y;
          const d = Math.sqrt(cdx * cdx + cdy * cdy);
          if (d < 120) {
            ctx.strokeStyle = lineBase + ((1 - d / 120) * lineMul) + ')';
            ctx.lineWidth = 0.5;
            ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y); ctx.stroke();
          }
        }
      }

      animationFrame = requestAnimationFrame(animate);
    };
    // Pause particles when Hero scrolls off-screen
    const io = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
      if (entry.isIntersecting && !animationFrame) {
        animationFrame = requestAnimationFrame(animate);
      }
    }, { threshold: 0 });
    if (containerRef.current) io.observe(containerRef.current);

    animate();

    return () => {
      io.disconnect();
      window.removeEventListener('resize', setCanvasDimensions);
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [isLight, isMobile, isHydrated]);

  if (!isHydrated) {
    return <Box sx={{ height: '100vh', bgcolor: 'background.default' }} />;
  }

  const handleMouseMove = (e) => {
    if (containerRef.current) {
      const { left, top, width, height } = containerRef.current.getBoundingClientRect();
      mousePositionRef.current = { x: (e.clientX - left) / width, y: (e.clientY - top) / height };
    }
  };

  return (
    <Box
      id="home"
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => { isHoveredRef.current = true; }}
      onMouseLeave={() => { isHoveredRef.current = false; }}
      sx={{
        minHeight: { xs: '100vh', md: 'calc(100vh + 95px)' },
        height: { xs: 'auto', md: 'calc(100vh + 95px)' },
        maxHeight: { xs: 'none', md: 'calc(100vh + 95px)' },
        position: 'relative',
        overflow: 'hidden',
        marginTop: { xs: '-56px', md: '-80px' },
        paddingTop: { xs: '56px', md: '80px' },
        display: 'flex',
        alignItems: { xs: 'flex-start', lg: 'center' },
        background: isLight
          ? 'linear-gradient(160deg, #f8f9ff 0%, #eef0fb 25%, #f0ecfa 45%, #e8eaf8 60%, #f1f5f9 80%, #fafaf9 100%)'
          : 'linear-gradient(135deg, #020617 0%, #0f172a 40%, #1e293b 70%, #0f172a 100%)',
        ...(isLight && {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: `
              radial-gradient(ellipse 700px 900px at 10% 30%, rgba(99, 102, 241, 0.12) 0%, transparent 55%),
              radial-gradient(circle 500px at 85% 15%, rgba(168, 85, 247, 0.08) 0%, transparent 50%),
              radial-gradient(ellipse 600px 800px at 75% 85%, rgba(59, 130, 246, 0.07) 0%, transparent 50%),
              radial-gradient(circle 350px at 50% 50%, rgba(129, 140, 248, 0.06) 0%, transparent 60%),
              radial-gradient(circle 250px at 25% 80%, rgba(192, 132, 252, 0.05) 0%, transparent 50%)
            `,
            zIndex: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: `
              radial-gradient(circle 180px at 18% 25%, rgba(99, 102, 241, 0.10) 0%, rgba(99, 102, 241, 0.02) 40%, transparent 70%),
              radial-gradient(circle 140px at 78% 18%, rgba(168, 85, 247, 0.09) 0%, rgba(168, 85, 247, 0.02) 35%, transparent 65%),
              radial-gradient(circle 200px at 65% 75%, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.01) 40%, transparent 70%),
              radial-gradient(circle 120px at 35% 65%, rgba(129, 140, 248, 0.07) 0%, transparent 60%),
              radial-gradient(circle 160px at 90% 55%, rgba(192, 132, 252, 0.06) 0%, transparent 55%)
            `,
            zIndex: 1,
            animation: 'heroOrbDrift 20s ease-in-out infinite alternate',
          },
          '@keyframes heroOrbDrift': {
            '0%': { transform: 'translate(0, 0) scale(1)' },
            '33%': { transform: 'translate(15px, -10px) scale(1.03)' },
            '66%': { transform: 'translate(-10px, 8px) scale(0.97)' },
            '100%': { transform: 'translate(5px, -5px) scale(1.01)' },
          },
        }),
        ...(!isLight && {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: `
              radial-gradient(circle at 20% 50%, rgba(56, 189, 248, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 75% 30%, rgba(16, 185, 129, 0.08) 0%, transparent 50%)
            `,
            zIndex: 1,
          },
        }),
      }}
    >
      {/* Particle Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute', top: 0, left: 0,
          width: '100%', height: '100%',
          zIndex: 2, pointerEvents: 'none', opacity: 0.75,
        }}
      />

      {/* Geometric accent — top right */}
      {!isMobile && (
        <Box sx={{
          position: 'absolute', top: 0, right: 0, width: 300, height: 300, zIndex: 1,
          '&::before': {
            content: '""', position: 'absolute', top: 20, right: 20,
            width: 60, height: 60,
            border: isLight ? '2px solid rgba(99, 102, 241, 0.15)' : '2.5px solid rgba(16, 185, 129, 0.35)',
            borderRight: 'none', borderBottom: 'none',
            transform: 'rotate(45deg)',
            boxShadow: isLight
              ? '0 0 20px rgba(99, 102, 241, 0.08)'
              : '0 0 25px rgba(16, 185, 129, 0.2)',
          },
          '&::after': {
            content: '""', position: 'absolute', top: 85, right: 85,
            width: 110, height: 110,
            border: isLight ? '1.5px solid rgba(99, 102, 241, 0.1)' : '1.5px solid rgba(16, 185, 129, 0.25)',
            borderRadius: '50%',
            background: isLight
              ? 'radial-gradient(circle, rgba(99, 102, 241, 0.04) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
            boxShadow: isLight
              ? 'inset 0 0 15px rgba(99, 102, 241, 0.06)'
              : 'inset 0 0 20px rgba(16, 185, 129, 0.12)',
          },
        }} />
      )}

      <Container
        maxWidth="xl"
        sx={{
          position: 'relative', zIndex: 3,
          px: { xs: 2, sm: 4, md: 6, lg: 8, xl: 12 },
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: 'center',
          gap: { xs: 4, lg: 6 },
          minHeight: { lg: 'calc(100vh - 120px)' },
          pt: { xs: 3, md: 12.5, lg: 12.5 },
          pb: { xs: 10, md: 12.5, lg: 12.5 },
        }}>
          {/* ── Left: Copy ── */}
          <Box sx={{
            flex: { xs: '1 1 100%', lg: '1 1 0' },
            minWidth: 0,
            py: { xs: 4, md: 0 },
            textAlign: { xs: 'center', lg: 'left' },
          }}>
            {/* Badge */}
            <Chip
              icon={<AutoAwesome sx={{ fontSize: '1.15rem' }} />}
              label="AI POWERED · ENTERPRISE INTEGRATION"
              sx={{
                mb: { xs: 2.5, md: 3 },
                px: 2.5, py: 0.75,
                fontSize: '0.73rem',
                background: isLight
                  ? 'rgba(255, 255, 255, 0.8)'
                  : 'rgba(16, 185, 129, 0.12)',
                backdropFilter: 'blur(20px)',
                border: isLight
                  ? '1.5px solid rgba(99, 102, 241, 0.2)'
                  : '1.5px solid rgba(16, 185, 129, 0.35)',
                borderRadius: '50px',
                color: isLight ? '#4338ca' : 'rgba(248,250,252,0.85)',
                fontWeight: 700,
                letterSpacing: '1.8px',
                '& .MuiChip-icon': { 
                  color: isLight ? '#6366f1' : '#10b981',
                  filter: isLight ? 'drop-shadow(0 0 3px rgba(99, 102, 241, 0.3))' : 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.4))',
                },
                boxShadow: isLight
                  ? '0 2px 12px rgba(99, 102, 241, 0.08), inset 0 1px 0 rgba(255,255,255,0.6)'
                  : '0 4px 24px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(16, 185, 129, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""', position: 'absolute', top: 0, left: '-75%',
                  width: '50%', height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                  transform: 'skewX(-20deg)',
                  animation: 'shine-sweep 3.5s ease-in-out infinite',
                },
              }}
            />

            {/* Headline */}
            <Box
              component="h1"
              sx={{
                fontFamily: '"Space Grotesk", "Inter", sans-serif',
                fontSize: { xs: '2.5rem', sm: '3.3rem', md: '4rem', lg: '4.4rem', xl: '4.8rem' },
                lineHeight: 1.02,
                mb: { xs: 2, md: 2.5 },
                letterSpacing: '-0.045em',
                fontWeight: 800,
                m: 0,
                color: isLight ? '#0a0f1a' : '#f8fafc',
                textTransform: 'uppercase',
                textShadow: isLight 
                  ? 'none'
                  : '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              The Fastest <Box component="span" sx={{
                  background: isLight
                    ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 40%, #818cf8 100%)'
                    : 'linear-gradient(135deg, #38bdf8, #8b5cf6)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: isLight ? 'drop-shadow(0 0 15px rgba(99, 102, 241, 0.15))' : 'drop-shadow(0 0 25px rgba(139, 92, 246, 0.25))',
                }}>
                  No-Code
                </Box> Integration Platform
          
            </Box>

            {/* Subtext */}
            <Typography
              variant="h6"
              component="p"
              sx={{
                fontSize: { xs: '1rem', md: '1.12rem' },
                fontWeight: 450,
                lineHeight: 1.65,
                mb: { xs: 2, md: 2.5 },
                color: isLight ? 'rgba(15, 23, 42, 0.7)' : 'rgba(248, 250, 252, 0.75)',
                maxWidth: { lg: 500 },
              }}
            >
              Build workflows visually, deploy them as compiled native code. An enterprise iPaaS that orchestrates AI agents, business logic, and 2,000+ data sources — at the speed of bespoke development, without writing a single line.
            </Typography>

            {/* Credibility line */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                mb: { xs: 3, md: 3.5 },
                px: 2,
                py: 0.75,
                borderRadius: '8px',
                background: isLight
                  ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.04) 0%, rgba(129, 140, 248, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.08) 100%)',
                border: isLight
                  ? '1px solid rgba(99, 102, 241, 0.12)'
                  : '1px solid rgba(16, 185, 129, 0.25)',
                boxShadow: isLight
                  ? '0 2px 8px rgba(99, 102, 241, 0.06), inset 0 1px 0 rgba(255,255,255,0.4)'
                  : '0 2px 12px rgba(16, 185, 129, 0.15)',
              }}
            >
              <Security sx={{ 
                fontSize: '1rem',
                color: isLight ? '#6366f1' : '#10b981',
                filter: isLight ? 'drop-shadow(0 0 3px rgba(99, 102, 241, 0.25))' : 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.4))',
              }} />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  color: isLight ? '#4338ca' : '#6ee7b7',
                  letterSpacing: '0.4px',
                }}
              >
                Enterprise-proven on-premise & cloud-native.
              </Typography>
            </Box>

            {/* CTAs */}
            <Stack
              direction="row"
              spacing={2}
              sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}
            >
              <MagneticButton>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<East sx={{ fontSize: '1.1rem' }} />}
                  onClick={() => window.open('https://studio.alphanetix.io', '_blank')}
                  sx={{
                    px: { xs: 4, sm: 5.5 },
                    py: { xs: 1.75, sm: 2.25 },
                    fontSize: { xs: '0.95rem', sm: '1.05rem' },
                    fontWeight: 800,
                    background: isLight
                      ? 'linear-gradient(135deg, #4338ca 0%, #4f46e5 40%, #6366f1 100%)'
                      : 'linear-gradient(135deg, #38bdf8 0%, #22d3ee 30%, #8b5cf6 100%)',
                    color: '#ffffff',
                    textTransform: 'uppercase',
                    letterSpacing: '1.2px',
                    border: isLight ? '1px solid rgba(255,255,255,0.15)' : 'none',
                    borderRadius: '16px',
                    boxShadow: isLight
                      ? '0 10px 35px rgba(79, 70, 229, 0.3), 0 4px 12px rgba(67, 56, 202, 0.2), inset 0 1px 0 rgba(255,255,255,0.12)'
                      : '0 10px 30px rgba(56, 189, 248, 0.45), inset 0 1px 0 rgba(255,255,255,0.15)',
                    transition: 'all 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""', position: 'absolute', top: 0, left: '-100%',
                      width: '100%', height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                      transition: 'left 0.6s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      background: isLight
                        ? 'linear-gradient(135deg, #3730a3 0%, #4338ca 40%, #4f46e5 100%)'
                        : 'linear-gradient(135deg, #22d3ee 0%, #38bdf8 30%, #a78bfa 100%)',
                      boxShadow: isLight
                        ? '0 15px 45px rgba(79, 70, 229, 0.4), 0 6px 15px rgba(67, 56, 202, 0.25)'
                        : '0 15px 40px rgba(56, 189, 248, 0.55)',
                      '&::before': { left: '100%' },
                    },
                    '&:active': { transform: 'translateY(-2px) scale(0.98)', transition: 'transform 0.1s ease' },
                  }}
                >
                  Try Alphanetix
                </Button>
              </MagneticButton>

             
            </Stack>
          </Box>

          {/* ── Right: Pipeline Animation (hero visual) ── */}
          <Box sx={{
            flex: { xs: '1 1 100%', lg: '0 0 540px' },
            display: 'flex',
            justifyContent: { xs: 'center', lg: 'flex-end' },
            alignItems: 'center',
            mt: { xs: 0, lg: 0 },
          }}>
            <Box sx={{
              width: '100%',
              maxWidth: { xs: '100%', sm: 480, lg: 540 },
              // Refined ambient glow
              filter: isLight 
                ? 'drop-shadow(0 0 80px rgba(99, 102, 241, 0.08))'
                : 'drop-shadow(0 0 60px rgba(16, 185, 129, 0.12))',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: '-30px',
                background: isLight
                  ? 'radial-gradient(ellipse at 40% 30%, rgba(99, 102, 241, 0.06) 0%, transparent 60%), radial-gradient(ellipse at 70% 70%, rgba(168, 85, 247, 0.04) 0%, transparent 60%)'
                  : 'radial-gradient(ellipse at center, rgba(16, 185, 129, 0.06) 0%, transparent 70%)',
                borderRadius: '24px',
                zIndex: -1,
              },
            }}>
              <HeroPipeline />
            </Box>
          </Box>
        </Box>
      </Container>

      {/* ── Scroll indicator ── */}
      <Box
        onClick={() => {
          scroller.scrollTo('why-alphanetix', {
            duration: 800,
            smooth: 'easeInOutQuart',
            offset: -80,
          });
        }}
        sx={{
          position: 'absolute',
          bottom: { xs: 16, md: 28 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'pointer',
          animation: 'heroScrollBounce 2s ease-in-out infinite',
          '@keyframes heroScrollBounce': {
            '0%, 100%': { transform: 'translateX(-50%) translateY(0)', opacity: 0.6 },
            '50%': { transform: 'translateX(-50%) translateY(8px)', opacity: 1 },
          },
          '&:hover .scroll-label': { opacity: 1 },
        }}
      >
        <Typography
          className="scroll-label"
          sx={{
            fontSize: '0.6rem', fontWeight: 600, letterSpacing: '2px',
            textTransform: 'uppercase',
            color: isLight ? 'rgba(99, 102, 241, 0.4)' : 'rgba(16, 185, 129, 0.5)',
            mb: 0.5, opacity: 0.5, transition: 'opacity 0.3s ease',
          }}
        >
          Scroll
        </Typography>
        <Box sx={{
          width: 24, height: 40, borderRadius: '12px',
          border: isLight
            ? '2px solid rgba(99, 102, 241, 0.2)'
            : '2px solid rgba(16, 185, 129, 0.3)',
          display: 'flex', justifyContent: 'center', pt: '6px',
        }}>
          <Box sx={{
            width: 3.5, height: 8, borderRadius: '2px',
            background: isLight ? 'rgba(99, 102, 241, 0.35)' : 'rgba(16, 185, 129, 0.5)',
            animation: 'heroScrollDot 2s ease-in-out infinite',
            '@keyframes heroScrollDot': {
              '0%': { opacity: 1, transform: 'translateY(0)' },
              '50%': { opacity: 0.3, transform: 'translateY(10px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }} />
        </Box>
      </Box>
    </Box>
  );
}

export default Hero;